import React, { useMemo } from "react";
import Select from "react-select";
import "../../styles/common/dashboardCourseSection.scss";

import ChartCard from "./charts/ChartCard";
import DoughnutChart from "./charts/DoughnutChart";
import BarChart from "./charts/BarChart";

const DashboardTeacherSection = ({
  teacherOptions,
  selectedTeacherOption,
  onChangeTeacher,
  teachers,
  teacherData,
  showSelectRow = true,
}) => {
  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
    }),
    [],
  );

  const safeOverview = teacherData?.teacher_overview || {};
  const safeContent = teacherData?.content_progress || {};
  const safeHomework = teacherData?.homework_summary || {};
  const safePerformance = teacherData?.student_performance || {};

  const completedPages = safeContent.completed_pages ?? 0;
  const totalPages = safeContent.total_pages ?? 0;
  const remainingPages = Math.max((totalPages ?? 0) - (completedPages ?? 0), 0);

  const studentsCompletedHomework =
    safeHomework.students_completed_homework ?? 0;
  const totalHomeworkCreated = safeHomework.total_homework_created ?? 0;

  const highPerfCount = Array.isArray(safePerformance.high_performing_subjects)
    ? safePerformance.high_performing_subjects.length
    : 0;
  const lowPerfCount = Array.isArray(safePerformance.low_performing_subjects)
    ? safePerformance.low_performing_subjects.length
    : 0;

  return (
    <section className="dashboard-course-section">
      <h2>Teacher</h2>
      {showSelectRow && (
        <div className="course-select-row">
          <label>Select teacher:</label>
          <div className="course-select-wrapper">
            <Select
              value={selectedTeacherOption}
              onChange={onChangeTeacher}
              options={teacherOptions}
              isDisabled={teachers.length === 0}
              isClearable={false}
            />
          </div>
        </div>
      )}

      <div className="course-metrics-grid">
        <div className="card metric-card">
          <div className="metric-label">Assigned Subjects</div>
          <div className="metric-value">
            {safeOverview.assigned_subjects ?? 0}
          </div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Assigned Batches</div>
          <div className="metric-value">
            {safeOverview.assigned_batches ?? 0}
          </div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Assigned Students</div>
          <div className="metric-value">
            {safeOverview.assigned_students ?? 0}
          </div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Homework Created</div>
          <div className="metric-value">{totalHomeworkCreated ?? 0}</div>
        </div>
      </div>

      <div className="dashboard-course-grid">
        <ChartCard title="Content Progress">
          <DoughnutChart
            data={{
              labels: ["Completed Pages", "Remaining Pages"],
              datasets: [
                {
                  data: [completedPages || 0, remainingPages],
                  backgroundColor: [
                    chartPalette.solid.lime,
                    chartPalette.solid.slate,
                  ],
                  hoverBackgroundColor: [
                    chartPalette.hover.lime,
                    chartPalette.hover.slate,
                  ],
                  borderColor: "rgba(15, 23, 42, 1)",
                  borderWidth: 0,
                },
              ],
            }}
          />
        </ChartCard>

        <ChartCard title="Homework Summary">
          <BarChart
            data={{
              labels: ["Homework Created", "Students Completed"],
              datasets: [
                {
                  label: "Count",
                  data: [
                    totalHomeworkCreated || 0,
                    studentsCompletedHomework || 0,
                  ],
                  backgroundColor: [
                    chartPalette.solid.orange,
                    chartPalette.solid.mint,
                  ],
                  hoverBackgroundColor: [
                    chartPalette.hover.orange,
                    chartPalette.hover.mint,
                  ],
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true } },
            }}
          />
        </ChartCard>

        <ChartCard title="Student Performance (Subjects)">
          <DoughnutChart
            data={{
              labels: ["High Performing", "Low Performing"],
              datasets: [
                {
                  data: [highPerfCount, lowPerfCount],
                  backgroundColor: [
                    chartPalette.solid.mint,
                    chartPalette.solid.orange,
                  ],
                  hoverBackgroundColor: [
                    chartPalette.hover.mint,
                    chartPalette.hover.orange,
                  ],
                  borderColor: "rgba(15, 23, 42, 1)",
                  borderWidth: 0,
                },
              ],
            }}
          />
        </ChartCard>
      </div>
    </section>
  );
};

export default DashboardTeacherSection;
